import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Device, View } from "@gooner/shared";
import { track } from "../../analytics";
import { type ViewInput, api } from "../../lib/api";
import { ViewCard } from "../components/ViewCard";
import { QuickSetups } from "../components/QuickSetups";
import { AmbientShowcase } from "../components/AmbientShowcase";
import { UsePhoneCard } from "../components/UsePhoneCard";
import { DiscordLink } from "../../components/DiscordLink";
import { AdSlot } from "../components/AdSlot";
import { SendToPopover } from "../components/SendToPopover";
import { useOnlineRetry } from "../hooks/useOnlineRetry";
import { CollectionGrid } from "../collections/CollectionCard";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Masked } from "../../lib/replayMask";
import { useDevicePresence } from "../hooks/useDevicePresence";
import { liveDevices, sweepDead } from "../lib/rail";
import { assignView } from "../lib/assign";
import { forgetTempView, recentTempViews, rememberTempView } from "../lib/recentTempViews";
import { forgetViewsCount, rememberViewsCount, skeletonViewCount } from "../lib/viewsSkeleton";
import { useSession } from "../../lib/session";
import { cx } from "../ui/primitives";
import { IconPlus } from "../ui/icons";
import { GuideInfoButton, SurfaceGuide, useSurfaceGuide } from "../../components/SurfaceGuide";
import type { CollectionOption } from "../lib/monitorTemplates";

/** A fresh, empty view needs a name — the server rejects a nameless one. */
const UNTITLED = "Untitled view";
/** What Build View starts you on: one blank 16:9 canvas, no regions. The editor's layout
 *  selector fills its preview slot, so "empty" is a state of one slot, not a screen. */
const BLANK: ViewInput = {
  name: UNTITLED,
  aspect: { w: 16, h: 9 },
  regions: [],
};

/**
 * Home. The library, and one way out to the remote.
 *
 * A view is what to show; a device is where it shows. Views fill the page; screens are
 * DRIVEN from the Goontroller (`/remote`), not from here. The desktop used to carry its
 * own Goon Controller rail — a card per connected screen with a `Showing:` select and a
 * code-entry card beside it — and it is gone on purpose: one controller, and it is the
 * one in your hand. What the desktop keeps is the QR that puts the remote there, and the
 * view-side gesture ("Send to") for the operator who is already looking at a view.
 *
 * The causal order is one-directional and the UI never breaks it: a view is useful with
 * no device (you can preview it where you built it), a device is useless with no view.
 * So views lead and pairing is never offered as a first step that dead-ends.
 */
export function Home() {
  const navigate = useNavigate();
  // This page's guide: auto-opens on arrival until "don't show this again", and the ⓘ
  // beside the heading brings it back. See guides.tsx for the why.
  const viewsGuide = useSurfaceGuide("views");
  const [views, setViews] = useState<View[] | null>(null);
  /**
   * Unsaved views this browser opened recently and that are still alive on the server.
   *
   * They have no row, so `GET /api/views` cannot return them — building one signed out
   * and pressing Back used to lose it outright, even though the entry survives five
   * minutes past its last touch. Resolved one by one because that IS the liveness check:
   * an expired entry 404s and is forgotten here rather than shown as a dead card.
   */
  const [unsaved, setUnsaved] = useState<View[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** The library fetch failing (offline, server down). Kept apart from `error`, which
   *  carries action failures (save, delete, copy), so Retry only shows for the load. */
  const [loadError, setLoadError] = useState<string | null>(null);
  /** True while a temporary view is being minted — the navigation that follows is a
   *  round trip, and a double-click would otherwise mint two. */
  const [minting, setMinting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<View | null>(null);
  /** The view whose "Send to" menu is open, and the button it hangs off. */
  const [sendTo, setSendTo] = useState<{
    view: View;
    anchor: HTMLElement;
  } | null>(null);
  const [collections, setCollections] = useState<CollectionOption[]>([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.views.list();
      // Cleared only with the answer in hand: on a Retry `views` is still the failed load's
      // [], and clearing first let the remember effect below save that as a count of 0.
      setLoadError(null);
      setViews([...res.views].sort((a, b) => b.updatedAt - a.updatedAt));
      void (async () => {
        const ids = recentTempViews();
        const alive: View[] = [];
        for (const id of ids) {
          try {
            alive.push((await api.views.get(id)).view);
          } catch {
            forgetTempView(id); // expired or swept — drop it rather than show a dead card
          }
        }
        setUnsaved(alive);
      })();
    } catch {
      // Friendly copy, same as Browse/Feed/Collections (!113): never surface a raw
      // fetch TypeError like "Failed to fetch".
      setLoadError("Couldn't load your views.");
      setViews([]);
    }
  }, []);
  const retryLoad = useCallback(() => {
    void load();
  }, [load]);
  // Back online (or Retry) re-fetches without a full reload.
  useOnlineRetry(retryLoad, loadError !== null);

  useEffect(() => {
    void load();
  }, [load]);
  // The next visit's skeleton (see GridSkeleton): how many saved views this identity has
  // now — after the list loads, and again after a delete or a copy, so the count is not
  // left a view or two out of date. A failed load shows an empty grid; that is not a count.
  useEffect(() => {
    if (views !== null && loadError === null) rememberViewsCount(views.length);
  }, [views, loadError]);

  // ---- screens ----
  //
  // No rail is drawn here any more, but the device state stays: it feeds the on-screens
  // pill on every view card, the "Send to" popover, and the sweep below — which is the
  // ONE client-side reaper of closed screens and has to run wherever the operator is.
  //
  // `devices` is every device row this operator has; `live` is only the ones a screen is
  // actually on right now. The two are deliberately different lists: see rail.ts for why
  // an offline row is litter rather than a screen waiting to return.
  const [devices, setDevices] = useState<Device[]>([]);
  const { presence, sendCommand } = useDevicePresence();

  const loadDevices = useCallback(async () => {
    try {
      setDevices((await api.devices.list()).devices);
    } catch {
      setDevices([]);
    }
  }, []);
  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);

  const live = liveDevices(devices, presence);
  /**
   * How many screens are showing this view, for the card's pill.
   *
   * One definition, two card lists. The unsaved row and the saved grid both render a
   * ViewCard and each had its own copy of this filter — identical today, and the sort of
   * pair where a later fix to one leaves the other quietly answering a different question.
   *
   * LIVE devices, not the listing: a row whose screen is off is not a screen this view is
   * on. That distinction is the whole content of the pill, and it is what must not fork.
   */
  const onScreensFor = (viewId: string) =>
    live.filter((d) => d.showing?.viewId === viewId).length;
  /** "Send to" lands here — the desktop's one assignment gesture, from the view's side.
   *  Per-screen driving (and Disconnect) is the remote's. */
  const assign = useCallback(
    async (deviceId: string, viewId: string | null) => {
      try {
        const device = await assignView(deviceId, viewId, sendCommand);
        setDevices((prev) =>
          prev.map((d) => (d.id === device.id ? device : d)),
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [sendCommand],
  );

  /** deviceId -> when presence first said it was offline. Not state: changing it must not
   *  re-render, and the timer below is the only reader. */
  const offlineSinceRef = useRef<Record<string, number>>({});
  // Latest inputs, read at tick time rather than captured at schedule time — the same
  // rule viewStore's `rev` guard exists to enforce, and for the same reason: a sweep
  // that fires on a snapshot from ten seconds ago would delete a screen that came back.
  const sweepInputs = useRef({ devices, presence });
  sweepInputs.current = { devices, presence };

  // On a timer rather than on the presence change, because the thing being measured is
  // elapsed time: the server pushes presence on join/leave/ping, so a screen that closed
  // produces one offline snapshot and then silence, and nothing would re-examine it.
  useEffect(() => {
    const t = setInterval(() => {
      const { devices: ds, presence: p } = sweepInputs.current;
      const { dead, offlineSince } = sweepDead(
        ds,
        p,
        offlineSinceRef.current,
        Date.now(),
      );
      offlineSinceRef.current = offlineSince;
      if (dead.length === 0) return;
      // Closing a screen destroys its device — every device, one lifetime. This is the
      // only thing that does it: the screen itself cannot, because `pagehide` cannot tell
      // a reload from a close. See rail.ts.
      void Promise.all(
        dead.map((id) => api.devices.remove(id).catch(() => undefined)),
      ).then(() =>
        setDevices((prev) => prev.filter((d) => !dead.includes(d.id))),
      );
    }, 2000);
    return () => clearInterval(t);
  }, []);

  // Template feeds name collections ("Just Butts", "Latinas"), so a setup resolves them
  // against what actually exists. Only the public list: a template names a curated
  // collection, and matching a user's own same-named playlist would quietly hand them a
  // different setup than the card describes.
  useEffect(() => {
    let ok = true;
    api
      .listCollections()
      .then((r) => {
        if (ok) {
          setCollections(
            r.collections.map((c) => ({
              id: c.id,
              name: c.name,
              clipCount: c.clipCount,
              editorsChoice: c.editorsChoice,
            })),
          );
        }
      })
      .catch(() => undefined);
    return () => {
      ok = false;
    };
  }, []);

  /** Build View: mint an unsaved view and go edit it. Nothing is written to the library. */
  const buildView = useCallback(async () => {
    setMinting(true);
    try {
      const { view } = await api.views.createTemp(BLANK);
      track("view_created", { surface: "builder" });
      // Remember it before navigating: this is the only moment its id is known to a page
      // that will still exist when the user comes back.
      rememberTempView(view.id);
      navigate(`/views/${view.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setMinting(false);
    }
  }, [navigate]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    try {
      await api.views.remove(id);
      setViews((prev) => prev?.filter((v) => v.id !== id) ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [pendingDelete]);

  /** Copy lands the new view at the top of Your views and leaves you where you are —
   *  copying several in a row is a natural thing to do, and being navigated into the
   *  editor after each one would interrupt it. */
  const onCopied = useCallback((view: View) => {
    setViews((prev) => (prev ? [view, ...prev] : [view]));
  }, []);

  return (
    <>
      {/* The ambient backdrop `/views` and the caves overview share (c91fe2e). Both of
          those screens are deleted by this task, and AmbientShowcase was extracted out of
          CaveOverview precisely so it would survive that — so this surface has to carry
          it, or the operator's treatment disappears with the files.

          Fixed below the 49px nav so it holds still while the library scrolls, with the
          content lifted to z-10 over it. It is pointer-events-none and positioned
          `absolute inset-0`, so it needs this positioned, clipped box to fill. */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 top-[49px] z-0 overflow-hidden bg-[var(--color-ink)]">
        <AmbientShowcase />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6 lg:flex-row lg:items-start">
        {/* No `order` juggling any more: the only thing beside the library is the QR card,
            and it hides itself on a phone (you are already the remote there), so the
            library is the whole page below lg and the left column above it. */}
        <main className="min-w-0 flex-1">
          <div className="mb-3 flex items-center gap-2">
            <h1 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
              Your views
            </h1>
            <GuideInfoButton onClick={viewsGuide.openInfo} label="How views work" />
          </div>

          {loadError ? (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              <span>{loadError}</span>
              <button
                type="button"
                onClick={retryLoad}
                className="shrink-0 rounded-md border border-red-500/40 px-2.5 py-1 text-xs font-medium text-red-200 transition-colors hover:bg-red-500/20"
              >
                Retry
              </button>
            </div>
          ) : null}
          {error ? (
            <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {views === null ? (
            <GridSkeleton />
          ) : (
            // The same responsive columns every collection listing uses. Importing the
            // component rather than copying its classes is what keeps the two grids
            // siblings when one of them is next restyled.
            <CollectionGrid>
              {/* Unsaved first: they are the most recent thing the user touched, and the
                  only ones that can disappear. Deleting one forgets it locally rather
                  than calling the API — there is no row to delete, and the entry expires
                  on its own. */}
              {unsaved.map((v) => (
                <ViewCard
                  key={v.id}
                  view={v}
                  onScreens={onScreensFor(v.id)}
                  onEdit={() => undefined}
                  onDelete={() => {
                    forgetTempView(v.id);
                    setUnsaved((prev) => prev.filter((x) => x.id !== v.id));
                  }}
                  onSendTo={(anchor) => setSendTo({ view: v, anchor })}
                  onOpened={(device) => setDevices((prev) => [...prev, device])}
                />
              ))}
              {views.map((v) => (
                <ViewCard
                  key={v.id}
                  view={v}
                  onScreens={onScreensFor(v.id)}
                  onEdit={
                    () =>
                      undefined /* the hover Edit link navigates; nothing to flush */
                  }
                  onDelete={() => setPendingDelete(v)}
                  onSendTo={(anchor) => setSendTo({ view: v, anchor })}
                  onOpened={(device) => setDevices((prev) => [...prev, device])}
                />
              ))}

              {/* LAST, after your views, not first. The tile is a destination rather than
                  a heading: putting it in reading position made the first thing on the
                  screen an empty box, and pushed the views the operator came for down a
                  row. After them it reads as "…and one more" — which is what it is.

                  Solid panel, solid border, full-strength text. It sits on a playing video
                  backdrop, and every softened value — dashes, a muted foreground, any
                  transparency at all — got read by that video as noise. Opaque is what
                  makes it look clickable here. It goes straight to the editor: no
                  intermediate page and no gallery to get past first. */}
              <button
                type="button"
                disabled={minting}
                onClick={() => void buildView()}
                className={cx(
                  "flex h-full min-h-[220px] w-full flex-col items-center justify-center gap-2 rounded-xl border p-5",
                  "border-[var(--color-edge)] bg-[var(--color-panel)] text-zinc-100 transition-colors",
                  "hover:border-[var(--color-accent)] hover:bg-[var(--color-panel-2)] disabled:opacity-60",
                )}
              >
                <IconPlus width={22} height={22} />
                <span className="text-sm font-semibold">
                  {minting ? "Opening…" : "Build View"}
                </span>
              </button>
            </CollectionGrid>
          )}

          {/* Below your views, never behind a click. A first-timer with nothing saved
              still sees the Build View tile and every setup on one screen. */}
          <QuickSetups
            collections={collections}
            onCopied={onCopied}
            /* No `onOpened` any more: a setup played from the gallery mints nothing. It
               opens a claimable `/screen`, so it joins the live list when somebody redeems
               its code on the remote — through the same path as any other claim — rather
               than the moment it is clicked. */
            onError={setError}
          />
        </main>

        {/* The desktop's whole relationship with screens, in one card: the QR that hands
            the remote to a phone. The Goon Controller rail that stood here (a card per
            connected screen, its `Showing:` select, Disconnect, and before that a
            code-entry card) is deleted on purpose — screens are driven from the remote
            alone, and a second controller on the desktop was a second place for the same
            state to be read differently.

            The ad below the card mounts unconditionally — a deliberate reversal,
            decided 2026-08-26. GoonFuel's fixed player CAN cover it on short
            viewports (an earlier attempt was removed for exactly that, 26% covered,
            measured), and a GoonFuel-aware mount gate was built and then ripped out
            on the owner's call: the player is allowed to block, and on tall monitors
            it doesn't. Desktop-only (lg + fine pointer), like the card. The rail is
            300px rather than the card's old 288 so the standard 300x250 unit fits
            unclipped. */}
        <div className="flex w-full shrink-0 flex-col gap-6 lg:w-[300px]">
          <UsePhoneCard />
          {/* Between the phone card and the ad, and gated like both of them
              (`pointer-coarse:hidden`): this rail is the desktop's column, and on a
              touch device the two things around this are already hidden — an ungated
              link here would be the only thing left in an otherwise empty rail. Phones
              get this link on the remote, where it belongs anyway. */}
          <DiscordLink className="pointer-coarse:hidden" />
          <AdSlot
            slot="viewsRail"
            size={{ width: 300, height: 250 }}
            className="hidden pointer-coarse:hidden lg:flex"
          />
        </div>

        {sendTo ? (
          <SendToPopover
            anchor={sendTo.anchor}
            devices={live}
            presence={presence}
            currentViewId={sendTo.view.id}
            onPick={(deviceId) => {
              void assign(deviceId, sendTo.view.id);
              setSendTo(null);
            }}
            onClose={() => setSendTo(null)}
          />
        ) : null}

        {viewsGuide.source ? (
          <SurfaceGuide surface="views" source={viewsGuide.source} onClose={viewsGuide.close} />
        ) : null}
        <ConfirmDialog
          open={!!pendingDelete}
          title="Delete view?"
          message={
            pendingDelete ? (
              // The view's name is the person's: masked in replay, the warning stays.
              <>“<Masked>{pendingDelete.name}</Masked>” will be permanently removed from your library. This cannot be undone.</>
            ) : (
              ""
            )
          }
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(null)}
        />
      </div>
    </>
  );
}

/**
 * The library's loading state, shaped like the page it turns into: one placeholder per
 * view this identity had last time (see viewsSkeleton.ts), each the shape of a ViewCard
 * (a 16:9 preview over its one footer row), then the Build View tile's 220px. A guest
 * with no count gets the Build View tile alone: three fixed cards made a phone's gallery
 * jump up by two cards' height when a new visitor's list arrived. A signed-in visitor
 * with no count (a fresh sign-in, a new browser) keeps the old three 220px cards, which
 * is no worse than before for anyone.
 */
function GridSkeleton() {
  const [count] = useState(skeletonViewCount);
  const tile = "animate-pulse rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)]";
  if (count === null) {
    return (
      <CollectionGrid>
        {Array.from({ length: LEGACY_SKELETON_CARDS }).map((_, i) => (
          <div key={i} aria-hidden className={cx("h-[220px]", tile)} />
        ))}
      </CollectionGrid>
    );
  }
  return (
    <CollectionGrid>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} aria-hidden className={tile}>
          <div className="aspect-video" />
          <div className="h-12" />
        </div>
      ))}
      <div aria-hidden className={cx("min-h-[220px]", tile)} />
    </CollectionGrid>
  );
}
/** The skeleton before counts were remembered: what a signed-in visitor with none gets. */
const LEGACY_SKELETON_CARDS = 3;

// The remembered count belongs to whoever was signed in. Signing out (or deleting the
// account) forgets it here, once for the whole app: Home is part of the control bundle, so
// this runs wherever the sign-out button is pressed, not only on /views.
useSession.subscribe((s, prev) => {
  if (prev.user && !s.user) forgetViewsCount();
});
